import { useTranslation } from "react-i18next";
import { Button, FilterableMultiSelect, Search } from "@carbon/react";
import { FilterOption } from "./useDetailItems";

export interface DetailItemFiltersProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  tagOptions: FilterOption[];
  selectedTagIds: string[];
  onTagIdsChange: (ids: string[]) => void;
  categoryOptions: FilterOption[];
  selectedCategoryIds: string[];
  onCategoryIdsChange: (ids: string[]) => void;
  buyerOptions: FilterOption[];
  selectedBuyerIds: string[];
  onBuyerIdsChange: (ids: string[]) => void;
  stateOptions: FilterOption[];
  selectedStates: string[];
  onStatesChange: (ids: string[]) => void;
  groupOptions?: FilterOption[];
  selectedGroups?: string[];
  onGroupsChange?: (ids: string[]) => void;
  hasAnyFilter: boolean;
  onClearAll: () => void;
}

/**
 * The detail item table's search + group/tag/category/assigned-buyer/state filters (Task 5) — rebuilt on
 * stock Carbon `Search`/`FilterableMultiSelect`, mirroring `PurchaseRequestListFilters.tsx`, in
 * place of the pre-Carbon screen's Tailwind `MultiSelectFilterPopover` popovers. Every filter and
 * the "clear all" affordance is preserved; each drives the same URL-param state as before.
 */
const DetailItemFilters = ({
  searchQuery,
  onSearchChange,
  tagOptions,
  selectedTagIds,
  onTagIdsChange,
  categoryOptions,
  selectedCategoryIds,
  onCategoryIdsChange,
  buyerOptions,
  selectedBuyerIds,
  onBuyerIdsChange,
  stateOptions,
  selectedStates,
  onStatesChange,
  groupOptions = [],
  selectedGroups = [],
  onGroupsChange,
  hasAnyFilter,
  onClearAll,
}: DetailItemFiltersProps) => {
  const { t } = useTranslation();

  const selected = (options: FilterOption[], ids: string[]) =>
    options.filter((option) => ids.includes(option.id));

  return (
    <div className="pr-detail__item-filters">
      <Search
        id="pr-detail-item-search"
        size="lg"
        labelText={t("purchaseRequests.search.items", {
          defaultValue: "Search items",
        })}
        placeholder={t("purchaseRequests.search.placeholder", {
          defaultValue: "Search by EAN or name...",
        })}
        value={searchQuery}
        onChange={(event) => onSearchChange(event.target.value)}
        onClear={() => onSearchChange("")}
      />

      <div className="pr-detail__item-filters-row">
        {groupOptions.length > 0 && onGroupsChange ? (
          <FilterableMultiSelect
            id="pr-detail-groups-filter"
            titleText={t("purchaseRequests.filters.groups")}
            placeholder={t("purchaseRequests.filters.search")}
            items={groupOptions}
            itemToString={(item: FilterOption | null) => item?.label ?? ""}
            selectedItems={selected(groupOptions, selectedGroups)}
            onChange={({ selectedItems }) =>
              onGroupsChange((selectedItems ?? []).map((item) => item.id))
            }
          />
        ) : null}

        <FilterableMultiSelect
          id="pr-detail-tags-filter"
          titleText={t("purchaseRequests.filters.tags")}
          placeholder={t("purchaseRequests.filters.searchTags")}
          items={tagOptions}
          itemToString={(item: FilterOption | null) => item?.label ?? ""}
          selectedItems={selected(tagOptions, selectedTagIds)}
          onChange={({ selectedItems }) =>
            onTagIdsChange((selectedItems ?? []).map((item) => item.id))
          }
        />

        <FilterableMultiSelect
          id="pr-detail-categories-filter"
          titleText={t("purchaseRequests.filters.categories")}
          placeholder={t("purchaseRequests.filters.searchCategories")}
          items={categoryOptions}
          itemToString={(item: FilterOption | null) => item?.label ?? ""}
          selectedItems={selected(categoryOptions, selectedCategoryIds)}
          onChange={({ selectedItems }) =>
            onCategoryIdsChange((selectedItems ?? []).map((item) => item.id))
          }
        />

        <FilterableMultiSelect
          id="pr-detail-buyers-filter"
          titleText={t("purchaseRequests.filters.assignedBuyers")}
          placeholder={t("purchaseRequests.filters.search")}
          items={buyerOptions}
          itemToString={(item: FilterOption | null) => item?.label ?? ""}
          selectedItems={selected(buyerOptions, selectedBuyerIds)}
          onChange={({ selectedItems }) =>
            onBuyerIdsChange((selectedItems ?? []).map((item) => item.id))
          }
        />

        <FilterableMultiSelect
          id="pr-detail-states-filter"
          titleText={t("purchaseRequests.filters.states")}
          placeholder={t("purchaseRequests.filters.search")}
          items={stateOptions}
          itemToString={(item: FilterOption | null) => item?.label ?? ""}
          selectedItems={selected(stateOptions, selectedStates)}
          onChange={({ selectedItems }) =>
            onStatesChange((selectedItems ?? []).map((item) => item.id))
          }
        />

        {hasAnyFilter ? (
          <Button kind="ghost" size="sm" onClick={onClearAll}>
            {t("purchaseRequests.filters.clearAll")}
          </Button>
        ) : null}
      </div>
    </div>
  );
};

export default DetailItemFilters;
